import useAppContext from 'flow/hooks/useAppContext';
import { isRetailer } from 'flow/selectors/selectors';
import {
    CreateSoundState,
    validSoundTypes,
    UpdateFormDataFunction,
} from 'flow/types/CreateSoundTypes';
import { MobileStatus } from 'flow/types/Extension';

import { TypedMap } from 'flow/types/TypedMap';
import { isTeamsTypeIntegration } from 'flow/utils/app-utils';
import { Retailers } from 'flow/utils/retailer-utils';
import React, { useEffect } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { FREEPRO_VOICEMAIL_SHORT_NUMBER } from '../../utils/constants/retailers/FreeProConstants';
import BetaBadge from '../badges/BetaBadge';
import SettingSection from '../Setting/SettingSection';
import TextToSpeech from '../TextToSpeech';
import BaseTextArea from '../ui-components/BaseTextArea';
import RadioButton from '../ui-components/RadioButton';
import FileUploadDropZone from './FileUploadDropZone';

type Props = {
    hasFixed: boolean;
    mobileStatus: TypedMap<MobileStatus>;
    formData: CreateSoundState;
    updateFormData: UpdateFormDataFunction;
    dropZoneRef: React.RefObject<HTMLLabelElement>;
    fileUploadRef: React.RefObject<HTMLInputElement>;
    isPrivate: boolean;
};

const CreateSound: React.FC<Props> = ({
    hasFixed,
    mobileStatus,
    formData,
    updateFormData,
    dropZoneRef,
    fileUploadRef,
    isPrivate,
}) => {
    const { selectedType, name, description, file } = formData;
    const { i18next, embedded } = useAppContext();

    const isRetailerFreePro = useAppSelector((state) =>
        isRetailer(state, Retailers.FREEPRO),
    );

    useEffect(() => {
        const dropZone = dropZoneRef.current;
        if (dropZone) {
            dropZone.addEventListener(
                'dragover',
                handleDragOver as EventListener,
            );
            dropZone.addEventListener('drop', handleDrop as EventListener);
        }
        return () => {
            if (dropZone) {
                dropZone.removeEventListener(
                    'dragover',
                    handleDragOver as EventListener,
                );
                dropZone.removeEventListener(
                    'drop',
                    handleDrop as EventListener,
                );
            }
        };
    }, []);

    const handleDragOver = (e: Event) => {
        e.preventDefault();
        const dragEvent = e as DragEvent;
        dragEvent.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop = (e: Event) => {
        e.preventDefault();
        const dragEvent = e as DragEvent;
        const droppedFile = dragEvent.dataTransfer.files[0];
        if (droppedFile && validSoundTypes.includes(droppedFile.type)) {
            updateFormData({ file: droppedFile });
        } else {
            console.error('Invalid file type dropped');
        }
    };

    const validFileFormat = (file: File | null): boolean => {
        return !!file && validSoundTypes.includes(file.type);
    };

    const mobileNumberIsVerified = (mobileStatus: TypedMap<MobileStatus>) => {
        if (!mobileStatus) {
            return false;
        }
        return !['nonverified', 'numbermissing'].includes(
            mobileStatus.get('mobileNumberState'),
        );
    };

    const hasMobile = mobileStatus?.has('number');
    const mobileIsVerified =
        mobileStatus && mobileNumberIsVerified(mobileStatus);

    useEffect(() => {
        if (selectedType === 'upload') {
            updateFormData({ file: null });
        }
    }, [selectedType]);

    return (
        <div className="flex flex-col gap-2">
            <div>
                <SettingSection>{`${i18next.t('input.title')} *`}</SettingSection>

                <BaseTextArea
                    rows={1}
                    value={name}
                    className="border border-gray-300"
                    onChange={(e) => updateFormData({ name: e.target.value })}
                />
            </div>
            <div>
                <SettingSection>
                    {i18next.t('input.description')}
                </SettingSection>

                <BaseTextArea
                    rows={3}
                    value={description}
                    onChange={(e) =>
                        updateFormData({ description: e.target.value })
                    }
                />
            </div>

            <RadioButton
                name="selectedType"
                checked={selectedType === 'upload'}
                onChange={() => updateFormData({ selectedType: 'upload' })}
            >
                {i18next.t('Recorder.Title')}
            </RadioButton>
            {selectedType === 'upload' && (
                <FileUploadDropZone
                    file={file}
                    className="ms-12"
                    validFileFormat={validFileFormat}
                    dropZoneRef={dropZoneRef}
                    fileUploadRef={fileUploadRef}
                    validSoundTypes={validSoundTypes}
                    onFileChange={(file) => updateFormData({ file })}
                />
            )}
            <RadioButton
                name="selectedType"
                checked={selectedType === 'tts'}
                onChange={() => updateFormData({ selectedType: 'tts' })}
            >
                <div className="flex items-center gap-x-4">
                    <p>{i18next.t('Recorder.tts')}</p>
                    <BetaBadge />
                </div>
            </RadioButton>
            {selectedType === 'tts' && (
                <TextToSpeech
                    className="ms-12"
                    setFile={(file) => updateFormData({ file })}
                />
            )}
            {!isTeamsTypeIntegration(embedded) && (
                <>
                    {hasFixed && (
                        <RadioButton
                            name="selectedType"
                            checked={selectedType === 'call-landline'}
                            onChange={() =>
                                updateFormData({
                                    selectedType: 'call-landline',
                                })
                            }
                        >
                            {i18next.t('Recorder.call.fixed')}
                        </RadioButton>
                    )}

                    {hasMobile && mobileIsVerified && (
                        <RadioButton
                            name="selectedType"
                            checked={selectedType === 'call-mobile'}
                            onChange={() =>
                                updateFormData({ selectedType: 'call-mobile' })
                            }
                        >
                            {i18next.t('Recorder.call.mobile')}
                        </RadioButton>
                    )}
                    {selectedType === 'call-mobile' && isPrivate && (
                        <div
                            key="verified-help-text"
                            className="ms-12 text-start text-sm text-gray-500"
                        >
                            {i18next.t('settings.voicemail.sound.helptext', {
                                number: isRetailerFreePro
                                    ? FREEPRO_VOICEMAIL_SHORT_NUMBER
                                    : '133',
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CreateSound;
